import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";

// WebSocket message types
interface SessionInfoMessage {
  type: "session_info";
  sessionId: string;
}

interface RawMessage {
  type: "raw";
  data: string;
}

type WebSocketMessage = SessionInfoMessage | RawMessage | Record<string, unknown>;

// Connection status types
type ConnectionStatus = "disconnected" | "connecting" | "connected" | "reconnecting" | "error" | "missing_tokens";

interface WebSocketContextValue {
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  lastMessage: WebSocketMessage | null;
  sessionId: string | undefined;
  connect: (jwtToken?: string, csrfToken?: string) => void;
  disconnect: () => void;
  sendMessage: (message: string | Record<string, unknown>) => boolean;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps): JSX.Element => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttempts = useRef<number>(0);
  const storedTokensRef = useRef<{ jwtToken: string; csrfToken: string } | null>(null);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds
  const reconnectIntervalDelay = 5000; // 5 seconds for interval-based reconnect

  const getWebSocketUrl = (): string => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/ws/webclient`;
  };

  // Start interval-based reconnection attempts
  const startReconnectInterval = (): void => {
    // Clear any existing interval
    if (reconnectIntervalRef.current) {
      clearInterval(reconnectIntervalRef.current);
    }

    setConnectionStatus("reconnecting");

    reconnectIntervalRef.current = setInterval(() => {
      // Check if already connected
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        if (reconnectIntervalRef.current) {
          clearInterval(reconnectIntervalRef.current);
          reconnectIntervalRef.current = null;
        }
        return;
      }

      // Try to reconnect using stored tokens
      if (storedTokensRef.current) {
        connect();
      }
    }, reconnectIntervalDelay);
  };

  // Stop interval-based reconnection
  const stopReconnectInterval = (): void => {
    if (reconnectIntervalRef.current) {
      clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = null;
    }
  };

  const connect = (jwtToken?: string, csrfToken?: string): void => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Store tokens from initial connect, reuse on reconnect
    if (jwtToken && csrfToken) {
      storedTokensRef.current = { jwtToken, csrfToken };
    }

    const actualJwtToken = jwtToken || storedTokensRef.current?.jwtToken;
    const actualCsrfToken = csrfToken || storedTokensRef.current?.csrfToken;

    if (!actualJwtToken || !actualCsrfToken) {
      setConnectionStatus("missing_tokens");
      return;
    }

    try {
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(
        actualJwtToken
      )}&csrf=${encodeURIComponent(actualCsrfToken)}`;

      setConnectionStatus("connecting");

      const ws = new WebSocket(wsUrl);

      ws.onopen = (): void => {
        setIsConnected(true);
        setConnectionStatus("connected");
        reconnectAttempts.current = 0;

        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // Stop interval-based reconnection if active
        stopReconnectInterval();
      };

      ws.onmessage = (event: MessageEvent): void => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Handle session info message specially
          if (message.type === "session_info") {
            setSessionId((message as SessionInfoMessage).sessionId);
          }

          setLastMessage(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
          setLastMessage({ type: "raw", data: event.data });
        }
      };

      ws.onclose = (event: CloseEvent): void => {
        setIsConnected(false);
        setConnectionStatus("disconnected");
        websocketRef.current = null;

        // Attempt reconnection if it wasn't a clean close (1000 = intentional close)
        if (event.code !== 1000) {
          if (storedTokensRef.current) {
            // Try immediate reconnect first (up to maxReconnectAttempts)
            if (reconnectAttempts.current < maxReconnectAttempts) {
              reconnectAttempts.current++;
              setConnectionStatus("reconnecting");

              reconnectTimeoutRef.current = setTimeout(() => {
                connect();
              }, reconnectDelay);
            } else {
              // After max attempts, switch to interval-based reconnection
              startReconnectInterval();
            }
          }
        }
      };

      ws.onerror = (): void => {
        setConnectionStatus("error");
      };

      websocketRef.current = ws;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setConnectionStatus("error");
    }
  };

  const disconnect = (): void => {
    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Stop interval-based reconnection
    stopReconnectInterval();

    // Reset reconnect attempts
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection

    if (websocketRef.current) {
      websocketRef.current.close(1000, "User logout"); // Clean close
      websocketRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus("disconnected");
    setLastMessage(null);
    setSessionId(undefined);
  };

  const sendMessage = (message: string | Record<string, unknown>): boolean => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      websocketRef.current.send(messageStr);
      return true;
    }
    return false;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const value: WebSocketContextValue = {
    isConnected,
    connectionStatus,
    lastMessage,
    sessionId,
    connect,
    disconnect,
    sendMessage,
    reconnectAttempts: reconnectAttempts.current,
    maxReconnectAttempts,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
